import {
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { Geometry, LineString, Point } from 'ol/geom';
import Map from 'ol/Map';
import { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { toLonLat } from 'ol/proj';

import {
  AssetMapBridgeService,
  AssetMapContext,
  AssetSavePayload,
} from 'src/app/components/admin/asset-management/asset-map-bridge.service';
import {
  AssetClassification,
  AssetLayer,
  AssetStatus,
} from 'src/app/shared/services/asset-management.service';
import { GeospatialMapBridgeService } from 'src/app/shared/services/geospatial-map-bridge.service';
import { SubscriptionAccessService } from 'src/app/shared/services/subscription-access.service';
import { LrsService } from 'src/app/shared/services/lrs.service';

@Component({
  selector: 'app-gis-asset-bridge-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gis-asset-bridge-panel.component.html',
  styleUrls: ['./gis-asset-bridge-panel.component.css'],
})
export class GisAssetBridgePanelComponent implements OnInit, OnDestroy {
  @Input() vectorSource!: VectorSource;
  /** When set, Save drawing also writes grade + depth into lrs.utility_assets. */
  @Input() lrsRouteId: number | null = null;

  amContext: AssetMapContext | null = null;
  amLayers: AssetLayer[] = [];
  amSelectedLayerId: number | null = null;
  amSelectedAssetId: number | null = null;
  amFeatureCount = 0;
  amStatus: 'idle' | 'loading' | 'saving' | 'error' = 'idle';
  amMessage = '';
  amPanelCollapsed = true;
  amShowSavePanel = false;
  amSearch = '';
  amVisibleAssets: Array<{
    id: number;
    name: string;
    code: string;
    classification: AssetClassification;
    category: string;
    status: AssetStatus;
    layerId: number | null;
    layerName: string;
  }> = [];

  amSaveDraft: AssetSavePayload & { layerId?: number | null } = {
    name: '',
    asset_code: '',
    description: '',
    classification: 'overground',
    status: 'planned',
    depth_m: null,
    elevation_m: null,
  };

  private map: Map | null = null;
  private amSource!: VectorSource;
  private amLayer!: VectorLayer<VectorSource>;
  private amClusterSource!: Cluster;
  private amClusterLayer!: VectorLayer<VectorSource>;
  private layerAttached = false;
  private mapClickKey: EventsKey | null = null;
  private stopClusterDistance: (() => void) | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly amBridge: AssetMapBridgeService,
    private readonly geoMapBridge: GeospatialMapBridgeService,
    private readonly subscriptionAccess: SubscriptionAccessService,
    private readonly lrs: LrsService
  ) {}

  /** GIS Asset Management is a Road-domain module (same gate as /api/asset-management). */
  get canUseAssetRegistry(): boolean {
    return this.subscriptionAccess.canAccessRoute('/asset-management');
  }

  ngOnInit(): void {
    const built = this.amBridge.createAssetLayer();
    this.amSource = built.source;
    this.amLayer = built.layer;
    this.amClusterSource = built.clusterSource;
    this.amClusterLayer = built.clusterLayer;

    this.geoMapBridge.mapReady$.pipe(takeUntil(this.destroy$)).subscribe((map) => {
      this.map = map;
      this.attachLayerToMap();
      this.initializeFromRoute();
    });

    const existing = this.geoMapBridge.getMap();
    if (existing) {
      this.map = existing;
      this.attachLayerToMap();
      this.initializeFromRoute();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.mapClickKey) {
      unByKey(this.mapClickKey);
      this.mapClickKey = null;
    }
    this.stopClusterDistance?.();
    this.stopClusterDistance = null;
    if (this.map && this.layerAttached) {
      this.map.removeLayer(this.amLayer);
      this.map.removeLayer(this.amClusterLayer);
    }
  }

  get amModeLabel(): string {
    if (!this.amContext) return '';
    return this.amContext.mode === 'create'
      ? 'Create'
      : this.amContext.mode === 'edit'
        ? 'Edit'
        : 'View';
  }

  get amFilteredAssets(): typeof this.amVisibleAssets {
    const q = (this.amSearch || '').trim().toLowerCase();
    if (!q) return this.amVisibleAssets;
    return this.amVisibleAssets.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.code.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q) ||
        a.status.toLowerCase().includes(q)
    );
  }

  amFocusAssetById(id: number): void {
    this.amSelectedAssetId = id;
    if (this.map) {
      this.amBridge.focusAsset(this.map, this.amSource, id);
    }
  }

  togglePanel(): void {
    this.amPanelCollapsed = !this.amPanelCollapsed;
    if (this.amPanelCollapsed) return;
    if (!this.canUseAssetRegistry) {
      this.amStatus = 'error';
      this.amMessage =
        'Road domain subscription required for GIS Asset Management (Road BoQ / GIS Asset).';
      return;
    }
    if (!this.amLayers.length) {
      this.primeLayersAndReload();
    }
  }

  amOnLayerChange(layerId: number | null): void {
    this.amSelectedLayerId = layerId;
    this.amReloadAssets(true);
  }

  amOpenSavePanel(): void {
    this.amShowSavePanel = true;
    this.amMessage = '';
  }

  amCloseSavePanel(): void {
    this.amShowSavePanel = false;
  }

  amSaveDrawingAsAsset(): void {
    const layerId =
      this.amSaveDraft.layerId != null
        ? Number(this.amSaveDraft.layerId)
        : this.amSelectedLayerId;
    if (!layerId || !this.map) {
      this.amMessage = 'Choose a layer before saving.';
      return;
    }
    const feat = this.pickCandidateFeature();
    if (!feat) {
      this.amMessage = 'Draw a feature on the map first.';
      return;
    }
    this.amStatus = 'saving';
    this.amMessage = '';
    this.amBridge
      .saveOlFeatureAsAsset(
        feat,
        layerId,
        {
          name: this.amSaveDraft.name,
          asset_code: this.amSaveDraft.asset_code,
          description: this.amSaveDraft.description,
          classification: this.amSaveDraft.classification,
          asset_category: this.amSaveDraft.asset_category,
          status: this.amSaveDraft.status,
          depth_m: this.amSaveDraft.depth_m ?? null,
          elevation_m: this.amSaveDraft.elevation_m ?? null,
          attributes: this.amSaveDraft.attributes,
        },
        this.map.getView().getProjection().getCode() || 'EPSG:3857'
      )
      .subscribe({
        next: (asset) => {
          this.amStatus = 'idle';
          this.amMessage = `Saved as asset #${asset.id}`;
          this.amSelectedAssetId = asset.id;
          this.amSelectedLayerId = asset.properties.layer_id;
          this.amShowSavePanel = false;
          if (this.vectorSource?.hasFeature(feat)) {
            this.vectorSource.removeFeature(feat);
          }
          this.amReloadAssets(true);
          this.syncSavedAssetToLrs(feat, {
            name: this.amSaveDraft.name,
            classification: this.amSaveDraft.classification,
            asset_category: this.amSaveDraft.asset_category,
            depth_m: this.amSaveDraft.depth_m ?? null,
          });
        },
        error: (err) => {
          this.amStatus = 'error';
          this.amMessage = err?.error?.message || err?.message || 'Save failed';
        },
      });
  }

  amUpdateFocusedGeometry(): void {
    if (!this.amSelectedAssetId || !this.map) {
      this.amMessage = 'No asset is currently focused.';
      return;
    }
    const feat = this.pickCandidateFeature();
    if (!feat) {
      this.amMessage = 'Draw the new geometry on the map first.';
      return;
    }
    this.amStatus = 'saving';
    this.amMessage = '';
    this.amBridge
      .updateAssetGeometryFromOl(
        this.amSelectedAssetId,
        feat,
        { change_summary: 'Geometry updated from geospatial editor' },
        this.map.getView().getProjection().getCode() || 'EPSG:3857'
      )
      .subscribe({
        next: () => {
          this.amStatus = 'idle';
          this.amMessage = 'Geometry updated';
          this.amReloadAssets(false);
        },
        error: (err) => {
          this.amStatus = 'error';
          this.amMessage = err?.error?.message || err?.message || 'Update failed';
        },
      });
  }

  amFocusSelected(): void {
    if (!this.amSelectedAssetId || !this.map) return;
    this.amBridge.focusAsset(this.map, this.amSource, this.amSelectedAssetId);
  }

  amDeleteSelected(): void {
    if (!this.amSelectedAssetId) return;
    if (!confirm(`Delete asset #${this.amSelectedAssetId}?`)) return;
    this.amStatus = 'saving';
    this.amBridge.deleteAsset(this.amSelectedAssetId).subscribe({
      next: () => {
        this.amStatus = 'idle';
        this.amMessage = 'Asset deleted';
        this.amSelectedAssetId = null;
        this.amReloadAssets(false);
      },
      error: (err) => {
        this.amStatus = 'error';
        this.amMessage = err?.error?.message || 'Delete failed';
      },
    });
  }

  private attachLayerToMap(): void {
    if (!this.map || this.layerAttached) return;
    this.map.addLayer(this.amLayer);
    this.map.addLayer(this.amClusterLayer);
    this.stopClusterDistance = this.amBridge.syncClusterDistance(this.map, this.amClusterSource);
    this.layerAttached = true;
    this.attachMapClickHandler();
  }

  private attachMapClickHandler(): void {
    if (!this.map || this.mapClickKey) return;
    this.mapClickKey = this.map.on('singleclick', (evt) =>
      this.onMapSingleClick(evt as MapBrowserEvent<PointerEvent>)
    );
  }

  private onMapSingleClick(evt: MapBrowserEvent<PointerEvent>): void {
    if (!this.map) return;
    let hitFeature: Feature<Geometry> | null = null;
    this.map.forEachFeatureAtPixel(
      evt.pixel,
      (feature) => {
        hitFeature = feature as Feature<Geometry>;
        return true;
      },
      {
        layerFilter: (layer) => layer === this.amLayer || layer === this.amClusterLayer,
        hitTolerance: 8,
      }
    );
    if (!hitFeature) return;
    const members = this.amBridge.featuresFromHit(hitFeature);
    if (members.length > 1) {
      this.amBridge.zoomToCluster(this.map, members);
      this.amMessage = `${members.length} assets — zoomed in`;
      return;
    }
    const target = members[0];
    const hitId = target ? this.amBridge.getAssetIdFromFeature(target) : null;
    if (hitId != null) {
      this.amPanelCollapsed = false;
      this.amFocusAssetById(hitId);
      this.amMessage = `Selected asset #${hitId}`;
    }
  }

  private initializeFromRoute(): void {
    this.amBridge.activateFromRoute(this.route).subscribe({
      next: (ctx) => {
        this.amContext = ctx;
        this.amSelectedAssetId = ctx?.assetId || null;
        this.amSelectedLayerId = ctx?.layerId || null;
        this.amPanelCollapsed = !ctx;
        if (ctx?.classification) {
          this.amSaveDraft.classification = ctx.classification;
        }
        if (ctx?.category) {
          this.amSaveDraft.asset_category = ctx.category;
        }
        if (ctx) {
          this.primeLayersAndReload(true);
        }
        if (ctx?.mode === 'create') {
          this.amShowSavePanel = true;
        }
      },
    });
  }

  private primeLayersAndReload(fitView = false): void {
    if (!this.canUseAssetRegistry) {
      this.amStatus = 'error';
      this.amMessage =
        'Road domain subscription required for GIS Asset Management (Road BoQ / GIS Asset).';
      return;
    }
    this.amBridge.primeLayers().subscribe({
      next: (layers) => {
        this.amLayers = layers;
        if (!this.amSelectedLayerId && layers.length) {
          this.amSelectedLayerId = layers[0].id;
        }
        this.amReloadAssets(fitView);
      },
      error: (err) => {
        this.amLayers = this.amBridge.listLayersCached();
        if (this.isForbidden(err)) {
          this.amStatus = 'error';
          this.amMessage = this.apiErrorMessage(err, 'Road domain subscription required');
          return;
        }
        this.amReloadAssets(fitView);
      },
    });
  }

  amReloadAssets(fitView = false): void {
    if (!this.amSource || !this.map) return;
    if (!this.canUseAssetRegistry) {
      this.amStatus = 'error';
      this.amMessage =
        'Road domain subscription required for GIS Asset Management (Road BoQ / GIS Asset).';
      return;
    }
    this.amStatus = 'loading';
    this.amMessage = '';
    this.amBridge
      .loadAssetsInto(this.amSource, {
        layerId: this.amSelectedLayerId,
        classification: this.amContext?.classification || null,
        mapProjection: this.map.getView().getProjection().getCode() || 'EPSG:3857',
      })
      .subscribe({
        next: (res) => {
          this.amFeatureCount = res.count;
          this.amStatus = 'idle';
          this.syncVisibleAssets();
          if (fitView && res.count > 0) {
            if (this.amSelectedAssetId) {
              this.amBridge.focusAsset(this.map!, this.amSource, this.amSelectedAssetId);
            } else {
              this.amBridge.fitAllAssets(this.map!, this.amSource);
            }
          }
        },
        error: (err) => {
          this.amStatus = 'error';
          this.amMessage = this.apiErrorMessage(err, 'Failed to load assets');
        },
      });
  }

  private isForbidden(err: { status?: number } | null | undefined): boolean {
    return err?.status === 403;
  }

  private apiErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) return body;
    return body?.error || body?.message || err?.message || fallback;
  }

  private pickCandidateFeature(): Feature<Geometry> | null {
    const drawingFeatures = this.vectorSource?.getFeatures() || [];
    if (drawingFeatures.length) {
      return drawingFeatures[drawingFeatures.length - 1];
    }
    return null;
  }

  private categoryToLrsUtility(category?: string | null): string {
    const t = String(category || '').toLowerCase();
    if (t.includes('water')) return 'water';
    if (t.includes('sewer')) return 'sewer';
    if (t.includes('storm')) return 'storm_drain';
    if (t.includes('ht') || t.includes('high')) return 'electric_ht';
    if (t.includes('electric') || t.includes('power')) return 'electric_lt';
    if (t.includes('gas') || t.includes('oil')) return 'oil_gas';
    if (t.includes('drain')) return 'drainage';
    return 'ofc';
  }

  private featureLonLatEnds(feat: Feature<Geometry>): { start: number[]; end: number[] } | null {
    const g = feat.getGeometry();
    if (!g) return null;
    if (g instanceof Point) {
      const ll = toLonLat(g.getCoordinates());
      return { start: ll, end: ll };
    }
    if (g instanceof LineString) {
      const coords = g.getCoordinates();
      if (!coords?.length) return null;
      return { start: toLonLat(coords[0]), end: toLonLat(coords[coords.length - 1]) };
    }
    const extent = g.getExtent();
    return {
      start: toLonLat([extent[0], extent[1]]),
      end: toLonLat([extent[2], extent[3]]),
    };
  }

  private syncSavedAssetToLrs(
    feat: Feature<Geometry>,
    payload: Pick<AssetSavePayload, 'name' | 'classification' | 'asset_category' | 'depth_m'>
  ): void {
    const routeId = this.lrsRouteId;
    if (!routeId) return;
    const ends = this.featureLonLatEnds(feat);
    if (!ends) return;
    const grade = payload.classification || 'overground';
    const depth =
      payload.depth_m != null
        ? payload.depth_m
        : grade === 'underground'
          ? 1.2
          : null;
    this.lrs.locate(routeId, ends.start[0], ends.start[1]).subscribe({
      next: (fromRes) => {
        this.lrs.locate(routeId, ends.end[0], ends.end[1]).subscribe({
          next: (toRes) => {
            const fromKm = fromRes.data.measure_km;
            const toKm = toRes.data.measure_km;
            const lo = Math.min(fromKm, toKm);
            const hi = Math.max(fromKm, toKm);
            const body: Record<string, unknown> = {
              utility_type: this.categoryToLrsUtility(payload.asset_category),
              measure_km: lo,
              label: payload.name || `AM ${grade}`,
              grade,
              classification: grade,
              depth_m: depth,
            };
            if (hi - lo > 0.001) body['measure_to_km'] = hi;
            this.lrs.createUtilityAsset(routeId, body).subscribe({
              next: () => {
                this.amMessage = `${this.amMessage} · synced to LRS route`.trim();
              },
              error: (e) => {
                this.amMessage = e?.error?.error || 'Asset saved; LRS sync skipped';
              },
            });
          },
        });
      },
    });
  }

  private syncVisibleAssets(): void {
    if (!this.amSource) {
      this.amVisibleAssets = [];
      return;
    }
    const next: typeof this.amVisibleAssets = [];
    for (const f of this.amSource.getFeatures()) {
      if (!this.amBridge.isBridgeFeature(f)) continue;
      const id = this.amBridge.getAssetIdFromFeature(f) ?? 0;
      const props = (f.get('amProperties') || {}) as Record<string, unknown>;
      const layerId = (props['layer_id'] as number) ?? null;
      const layer = this.amBridge.getLayer(layerId);
      next.push({
        id,
        name: String(props['name'] || props['asset_code'] || `Asset #${id}`),
        code: String(props['asset_code'] || ''),
        classification: (props['classification'] || 'overground') as AssetClassification,
        category: String(props['asset_category'] || '—'),
        status: (props['status'] || 'planned') as AssetStatus,
        layerId,
        layerName: layer?.name || '—',
      });
    }
    next.sort((a, b) => a.name.localeCompare(b.name));
    this.amVisibleAssets = next;
  }
}
